import React, { useState, useEffect } from 'react';
import { Percent, RefreshCw } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';
import { getAccountTypeColor, getTaxRateStatusColor } from '../../utils/statusColors';
import { IntegrationSourceBadge } from './IntegrationSourceBadge';

interface TaxRate {
  _id: string;
  name: string;
  taxType: string;
  rate?: number;
  effectiveRate?: number;
  status: string;
  isActive: boolean;
  syncedFromXero: boolean;
  syncedFromMYOB?: boolean;
  syncedFromQuickBooks?: boolean;
  syncedFromZohoBooks?: boolean;
  syncedFrom?: "xero" | "myob" | "quickbooks" | "zohobooks";
  canApplyToRevenue?: boolean;
  canApplyToExpenses?: boolean;
  lastSyncedAt?: string;
}

export const TaxRates: React.FC = () => {
  const { t } = useTranslation();
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchTaxRates();
  }, []);

  const fetchTaxRates = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/tax-rates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setTaxRates(data.data);
      } else {
        setError(data.message || 'Failed to fetch tax rates');
      }
    } catch (err) {
      console.error('Fetch tax rates error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatRate = (rate?: number) => {
    if (rate === undefined || rate === null) return t('admin.taxRates.notAvailable');
    return `${rate.toFixed(2)}%`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('admin.taxRates.never');
    return formatDateUtil(dateString);
  };

  const getIntegrationSource = (taxRate: TaxRate) => {
    if (taxRate.syncedFrom) {
      return taxRate.syncedFrom;
    }
    // Fallback to legacy fields
    if (taxRate.syncedFromXero) return "xero";
    if (taxRate.syncedFromMYOB) return "myob";
    if (taxRate.syncedFromQuickBooks) return "quickbooks";
    if (taxRate.syncedFromZohoBooks) return "zohobooks";
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-lg bg-purple-50 dark:bg-gray-700 dark:border dark:border-purple-800/50 flex items-center justify-center mr-4">
            <Percent className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('admin.taxRates.title')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('admin.taxRates.description')}
            </p>
          </div>
        </div>
        
        <button
          onClick={fetchTaxRates}
          disabled={loading}
          className="flex items-center px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('admin.taxRates.refresh')}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('admin.taxRates.loading')}</p>
        </div>
      ) : taxRates.length === 0 ? (
        <div className="text-center py-8">
          <Percent className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('admin.taxRates.noTaxRates')}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.name')}
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.type')}
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.rate')}
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.revenue')}
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.expenses')}
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.status')}
                </th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('admin.taxRates.tableHeaders.source')}
                </th>
              </tr>
            </thead>
            <tbody>
              {taxRates.map((taxRate) => (
                <tr
                  key={taxRate._id}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {taxRate.name}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getAccountTypeColor()}`}>
                      {taxRate.taxType}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {formatRate(taxRate.rate || taxRate.effectiveRate)}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {taxRate.canApplyToRevenue ? (
                      <span className="text-success-600 dark:text-success-400">✓</span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {taxRate.canApplyToExpenses ? (
                      <span className="text-success-600 dark:text-success-400">✓</span>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getTaxRateStatusColor(taxRate.isActive, taxRate.status)}`}>
                      {taxRate.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <IntegrationSourceBadge source={getIntegrationSource(taxRate)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {taxRates.length > 0 && (
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          {t('admin.taxRates.showing').replace('{count}', taxRates.length.toString()).replace('{plural}', taxRates.length !== 1 ? 's' : '')}
        </div>
      )}
    </div>
  );
};

